import React, { useState, useEffect, useMemo } from 'react';
import { journalService } from '../services/adminService';
import { uploadPdfToCode } from '../services/codeDataStore';
import { sortArticlesBySequence, saveLocalJournals } from '../services/firebaseService';
import DataImport from './DataImport';
import { hasFile, getFileInfo, formatFileSize } from '../utils/fileUtils';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaFilePdf,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaUpload,
  FaExternalLinkAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import { formatVolumeIssue } from '../utils/issueFormat';
import { readDatesFromPdf, readDatesFromPdfUrl } from '../utils/pdfDates';
import { getBestFileUrl } from '../utils/fileUrlConverter';

const ARTICLE_TYPES = [
  { value: 'originalArticles', label: 'Original Research Articles' },
  { value: 'reviewArticles', label: 'Review Articles' },
  { value: 'caseReports', label: 'Case Studies' },
  { value: 'editorialNote', label: 'Editorial Note' },
  { value: 'philosophicalInsights', label: 'Philosophical Insights' },
  { value: 'seminarReport', label: 'Seminar Report' }
];

const emptyArticleForm = {
  title: '',
  authors: '',
  volume: '1',
  issue: '1',
  date: new Date().toISOString().split('T')[0],
  receivedDate: '',
  revisedDate: '',
  acceptedDate: '',
  finalAcceptedDate: '',
  firstPage: '',
  lastPage: '',
  pageCount: '',
  fileLink: '',
  type: 'originalArticles',
  abstract: '',
  keywords: '',
  affiliation: '',
  articleNumber: '1',
  isCurrentIssue: true,
  isArchived: false
};

const ArticlesAdmin = () => {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'CURRENT' | 'ARCHIVED'

  // Import Drawer
  const [showImport, setShowImport] = useState(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyArticleForm);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadFailed, setUploadFailed] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      alert('Please select a valid PDF file.');
      return;
    }

    setUploadFailed(false);
    setUploadingPdf(true);
    setUploadStatus('Uploading PDF...');

    try {
      // Read the Received / Accepted / Published dates printed in the PDF
      let found = {};
      try {
        found = await readDatesFromPdf(file);
      } catch (readErr) {
        console.warn('Could not read dates from the PDF:', readErr);
      }
      const { url, filename } = await uploadPdfToCode(file);
      setFormData(prev => ({
        ...prev,
        fileLink: url,
        friendlyUrl: filename.replace(/\.pdf$/i, ''),
        ...fillDates(prev, found)
      }));
      setUploadStatus(`PDF uploaded. ${describeDates(found)} Save the article to keep it.`);
    } catch (err) {
      setUploadFailed(true);
      setUploadStatus(err.message || 'PDF upload failed.');
    } finally {
      setUploadingPdf(false);
    }
  };

  useEffect(() => {
    fetchJournals();
  }, []);

  const fetchJournals = async () => {
    setLoading(true);
    try {
      const data = await journalService.getAllJournals();
      setJournals(sortArticlesBySequence(data || []));
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to fetch articles from database.');
    }
    setLoading(false);
  };

  // Convert date format for HTML date input
  const convertDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Last page = first page + the PDF's page count - 1
  const lastPageFor = (firstPage, pageCount) => {
    const first = parseInt(firstPage, 10);
    const count = parseInt(pageCount, 10);
    return first > 0 && count > 0 ? String(first + count - 1) : '';
  };

  // Dates and page count found in a PDF fill the form; '' means "not printed in the PDF"
  const fillDates = (prev, found) => {
    const pageCount = found.pageCount ? String(found.pageCount) : prev.pageCount || '';
    return {
      receivedDate: found.receivedDate || prev.receivedDate || '',
      revisedDate: found.revisedDate || prev.revisedDate || '',
      acceptedDate: found.acceptedDate || prev.acceptedDate || '',
      finalAcceptedDate: found.finalAcceptedDate || prev.finalAcceptedDate || '',
      date: found.publishedDate || prev.date || '',
      pageCount,
      lastPage: lastPageFor(prev.firstPage, pageCount) || prev.lastPage || '',
    };
  };
  const describeDates = (found) => {
    const parts = [
      found.receivedDate && `Received ${found.receivedDate}`,
      found.revisedDate && `Resubmitted ${found.revisedDate}`,
      found.acceptedDate && `Accepted ${found.acceptedDate}`,
      found.finalAcceptedDate && `Final acceptance ${found.finalAcceptedDate}`,
      found.publishedDate && `Published ${found.publishedDate}`,
    ].filter(Boolean);
    const pages = found.pageCount ? ` The PDF has ${found.pageCount} page(s).` : '';
    return (parts.length ? `Dates read from the PDF: ${parts.join(', ')}.` : 'No dates found in the PDF.') + pages;
  };
  // Same-origin address first (aimjhm.com/journal/<name>.pdf), then the stored link
  const pdfUrlsFor = (article) => {
    const friendly = String(article.friendlyUrl || '').replace(/\.pdf$/i, '');
    return [friendly && `/journal/${friendly}.pdf`, getBestFileUrl(article), article.fileLink, article.pdfLink];
  };

  const [readingDates, setReadingDates] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');

  const handleReadDatesForForm = async () => {
    setReadingDates(true);
    try {
      const found = await readDatesFromPdfUrl(pdfUrlsFor(formData));
      setFormData((prev) => ({ ...prev, ...fillDates(prev, found) }));
      setUploadFailed(false);
      setUploadStatus(`${describeDates(found)} Check them and save the article.`);
    } catch (err) {
      setUploadFailed(true);
      setUploadStatus(`Could not read the PDF: ${err.message}`);
    } finally {
      setReadingDates(false);
    }
  };

  // Reads every article's PDF and fills the dates and page numbers that are still empty, then saves once.
  // Page numbers run on through each issue in the display order: 1-1, 2-16, 17-21, ...
  const handleFillAllDates = async () => {
    if (!window.confirm('Read the PDF of every article and fill in the dates and page numbers that are still empty? Page numbers run on through each issue in the display order. Values already entered are kept.')) return;
    setBulkStatus('Reading PDFs...');
    const DATE_FIELDS = ['receivedDate', 'revisedDate', 'acceptedDate', 'finalAcceptedDate'];
    const PAGE_FIELDS = ['pageCount', 'firstPage', 'lastPage'];
    const ordered = sortArticlesBySequence(journals);
    const nextPage = {};
    const byId = {};
    let filled = 0;
    for (let i = 0; i < ordered.length; i++) {
      const article = ordered[i];
      setBulkStatus(`Reading PDF ${i + 1} of ${ordered.length}...`);
      let found = {};
      try {
        found = await readDatesFromPdfUrl(pdfUrlsFor(article));
      } catch (err) {
        console.warn(`Could not read the PDF of "${article.title}":`, err);
      }
      const next = { ...article };
      DATE_FIELDS.forEach((field) => {
        next[field] = article[field] || found[field] || '';
      });
      next.pageCount = String(article.pageCount || found.pageCount || '');
      const issueKey = `${article.volume}-${article.issue}`;
      if (!next.firstPage && next.pageCount) next.firstPage = String(nextPage[issueKey] || 1);
      if (!next.lastPage) next.lastPage = lastPageFor(next.firstPage, next.pageCount);
      if (parseInt(next.lastPage, 10) > 0) nextPage[issueKey] = parseInt(next.lastPage, 10) + 1;
      if ([...DATE_FIELDS, ...PAGE_FIELDS].some((field) => String(next[field] || '') !== String(article[field] || ''))) filled++;
      byId[article.id] = next;
    }
    try {
      if (filled > 0) await saveLocalJournals(journals.map((article) => byId[article.id] || article));
      setBulkStatus('');
      setSuccess(filled > 0 ? `Dates and page numbers filled from the PDFs for ${filled} article(s). They are live now.` : 'Nothing new found in the PDFs.');
      fetchJournals();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setBulkStatus('');
      setError(err.message || 'Could not save the dates.');
    }
  };

  const handleOpenAdd = () => {
    setFormData({
      ...emptyArticleForm,
      articleNumber: (journals.length + 1).toString(),
      date: new Date().toISOString().split('T')[0]
    });
    setModalMode('add');
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (journal) => {
    setFormData({
      title: journal.title || '',
      authors: journal.authors || '',
      volume: journal.volume || '1',
      issue: journal.issue || '1',
      date: convertDateForInput(journal.date),
      receivedDate: convertDateForInput(journal.receivedDate),
      revisedDate: convertDateForInput(journal.revisedDate),
      acceptedDate: convertDateForInput(journal.acceptedDate),
      finalAcceptedDate: convertDateForInput(journal.finalAcceptedDate),
      firstPage: journal.firstPage || '',
      lastPage: journal.lastPage || '',
      pageCount: journal.pageCount || '',
      friendlyUrl: journal.friendlyUrl || '',
      fileLink: journal.fileLink || '',
      type: journal.type || 'originalArticles',
      abstract: journal.abstract || '',
      keywords: journal.keywords || '',
      affiliation: journal.affiliation || '',
      articleNumber: journal.articleNumber || '1',
      isCurrentIssue: journal.isCurrentIssue || false,
      isArchived: journal.isArchived || false
    });
    setModalMode('edit');
    setEditingId(journal.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData(emptyArticleForm);
    setEditingId(null);
  };

  const handleSaveModal = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Please enter article title.');
      return;
    }
    if (!formData.authors.trim()) {
      alert('Please enter authors.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (modalMode === 'edit' && editingId) {
        await journalService.updateJournal(editingId, formData);
        setSuccess(`Updated "${formData.title}"`);
      } else {
        await journalService.addJournal(formData);
        setSuccess(`Added new article "${formData.title}"`);
      }
      handleCloseModal();
      fetchJournals();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save article.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title || 'this article'}"?`)) {
      return;
    }
    setLoading(true);
    try {
      await journalService.deleteJournal(id);
      setSuccess('Article deleted successfully!');
      fetchJournals();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to delete article.');
    }
    setLoading(false);
  };

  // Filtered Journals List
  const filteredJournals = useMemo(() => {
    const list = journals.filter((j) => {
      // Type Filter
      if (typeFilter !== 'ALL' && j.type !== typeFilter) {
        return false;
      }

      // Status Filter
      if (statusFilter === 'CURRENT' && !j.isCurrentIssue) return false;
      if (statusFilter === 'ARCHIVED' && !j.isArchived) return false;

      // Search Query
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (j.title && j.title.toLowerCase().includes(q)) ||
        (j.authors && j.authors.toLowerCase().includes(q)) ||
        (j.volume && j.volume.toString().toLowerCase().includes(q)) ||
        (j.issue && j.issue.toString().toLowerCase().includes(q)) ||
        (j.affiliation && j.affiliation.toLowerCase().includes(q)) ||
        (j.keywords && j.keywords.toLowerCase().includes(q))
      );
    });
    return sortArticlesBySequence(list);
  }, [journals, typeFilter, statusFilter, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Top Banner & Control Bar */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-serif font-bold text-[#0B3B60]">
              Articles & Journals Directory
            </h2>
            <span className="text-xs bg-blue-100 text-[#03478E] font-bold px-2.5 py-0.5 rounded-full">
              {journals.length} Total
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Manage published research manuscripts, abstracts, PDF attachments, and issue assignments.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={() => setShowImport(!showImport)}
            className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl text-xs font-bold shadow-2xs transition"
          >
            <FaUpload className="text-sm text-[#03478E]" />
            <span>{showImport ? 'Hide Import' : 'Import / Bulk Upload'}</span>
          </button>

          <button
            type="button"
            onClick={handleFillAllDates}
            disabled={!!bulkStatus || journals.length === 0}
            className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-700 px-4 py-2.5 rounded-xl text-xs font-bold shadow-2xs transition"
          >
            <FaCalendarAlt className="text-sm text-[#03478E]" />
            <span>{bulkStatus || 'Fill Dates & Pages from PDFs'}</span>
          </button>

          <button
            type="button"
            onClick={handleOpenAdd}
            className="inline-flex items-center gap-2 bg-[#03478E] hover:bg-[#023366] text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm transition"
          >
            <FaPlus className="text-sm" />
            <span>Add New Article</span>
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
          <FaExclamationCircle className="text-base flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
          <FaCheckCircle className="text-base flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Bulk Import Drawer */}
      {showImport && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <DataImport onImportComplete={() => {
            fetchJournals();
            setShowImport(false);
          }} />
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Status & Type Filter Chips */}
        <div className="flex flex-wrap items-center gap-1.5 flex-1">
          <button
            type="button"
            onClick={() => { setStatusFilter('ALL'); setTypeFilter('ALL'); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              statusFilter === 'ALL' && typeFilter === 'ALL'
                ? 'bg-[#03478E] text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Articles ({journals.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('CURRENT')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              statusFilter === 'CURRENT'
                ? 'bg-[#03478E] text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Current Issue Only
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('ARCHIVED')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              statusFilter === 'ARCHIVED'
                ? 'bg-[#03478E] text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Archived Only
          </button>

          {/* Type Dropdown Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-xs p-1.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium focus:outline-none"
          >
            <option value="ALL">Filter by Article Type...</option>
            {ARTICLE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-64 flex-shrink-0">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search title, author, volume..."
            className="w-full text-xs pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 focus:bg-white"
          />
          <FaSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
        </div>
      </div>

      {/* Articles Directory List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400 font-medium">
          Loading published journals...
        </div>
      ) : filteredJournals.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-500">
          <p className="text-sm font-semibold">No articles found matching your criteria.</p>
          <p className="text-xs text-gray-400 mt-1">Try clearing filters or search query.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredJournals.map((journal, idx) => {
            const fileInfo = hasFile(journal) ? getFileInfo(journal) : null;
            const typeObj = ARTICLE_TYPES.find((t) => t.value === journal.type);
            const typeLabel = typeObj ? typeObj.label : (journal.type || 'Original Research');

            return (
              <div
                key={journal.id || idx}
                className="bg-white rounded-2xl p-5 shadow-xs border border-gray-200 hover:border-blue-200 hover:shadow-sm transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Left: Article Details */}
                  <div className="space-y-1.5 flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        #{journal.articleNumber || idx + 1}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-blue-50 text-[#03478E]">
                        {typeLabel}
                      </span>
                      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {formatVolumeIssue(journal.volume, journal.issue)}
                      </span>
                      {journal.isCurrentIssue && (
                        <span className="text-[10px] font-extrabold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                          Current Issue
                        </span>
                      )}
                      {journal.isArchived && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-500 text-white px-2 py-0.5 rounded-full">
                          Archived
                        </span>
                      )}
                    </div>

                    <h3 className="text-base font-serif font-bold text-gray-900 leading-snug">
                      {journal.title}
                    </h3>

                    <p className="text-xs text-gray-600 font-medium">
                      By {journal.authors}
                    </p>

                    {journal.affiliation && (
                      <p className="text-xs text-gray-500 italic">
                        {journal.affiliation}
                      </p>
                    )}

                    {/* Metadata & File Info */}
                    <div className="flex items-center gap-4 text-[11px] text-gray-500 pt-1 flex-wrap">
                      {journal.date && (
                        <span className="flex items-center gap-1">
                          <FaCalendarAlt className="text-gray-400" />
                          {journal.date}
                        </span>
                      )}

                      {fileInfo && (
                        <a
                          href={fileInfo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#03478E] hover:underline font-semibold"
                        >
                          <FaFilePdf className="text-red-500" />
                          <span>{fileInfo.name}</span>
                          {fileInfo.size && (
                            <span className="text-gray-400 font-mono">
                              ({formatFileSize(fileInfo.size)})
                            </span>
                          )}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0 border-t md:border-t-0 pt-3 md:pt-0 justify-end">
                    {journal.fileLink && (
                      <a
                        href={journal.fileLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Get PDF"
                        className="p-2 text-gray-600 hover:text-[#03478E] hover:bg-gray-100 rounded-xl text-xs transition"
                      >
                        <FaExternalLinkAlt className="text-xs" />
                      </a>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOpenEdit(journal)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-[#03478E] text-[#03478E] hover:text-white rounded-xl text-xs font-bold transition shadow-2xs"
                    >
                      <FaEdit className="text-xs" />
                      <span>Edit</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(journal.id, journal.title)}
                      title="Delete Article"
                      className="p-2 text-red-500 hover:bg-red-50 hover:text-red-700 rounded-xl text-xs transition"
                    >
                      <FaTrash className="text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ======================= ADD / EDIT ARTICLE MODAL ======================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl overflow-hidden border border-gray-100 my-8">
            <div className="bg-[#03478E] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-serif font-bold text-lg">
                  {modalMode === 'edit' ? 'Edit Article Details' : 'Add New Journal Article'}
                </h3>
                <p className="text-xs text-blue-100">
                  {modalMode === 'edit' ? 'Update metadata, PDF URL, and issue settings' : 'Publish a new research paper into the repository'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 text-sm"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSaveModal} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Title */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Article Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Clinical Efficacy of Homoeopathic Remedies in Diabetic Neuropathy"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Authors */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Authors *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.authors}
                    onChange={(e) => setFormData({ ...formData, authors: e.target.value })}
                    placeholder="e.g. Dr. A. Sharma, Dr. S. Patel"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Article Type */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Article Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  >
                    {ARTICLE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Volume & Issue */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Volume *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.volume}
                    onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                    placeholder="e.g. 1"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Issue *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.issue}
                    onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                    placeholder="e.g. 1"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Received / Resubmission / Accepted / Final Acceptance dates (read from the PDF, see "Read dates from PDF") */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Received Date
                  </label>
                  <input
                    type="date"
                    value={formData.receivedDate || ''}
                    onChange={(e) => setFormData({ ...formData, receivedDate: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Resubmission Date
                  </label>
                  <input
                    type="date"
                    value={formData.revisedDate || ''}
                    onChange={(e) => setFormData({ ...formData, revisedDate: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Accepted Date
                  </label>
                  <input
                    type="date"
                    value={formData.acceptedDate || ''}
                    onChange={(e) => setFormData({ ...formData, acceptedDate: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Final Acceptance Date
                  </label>
                  <input
                    type="date"
                    value={formData.finalAcceptedDate || ''}
                    onChange={(e) => setFormData({ ...formData, finalAcceptedDate: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Pages in the issue: the last page follows from the PDF's page count */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    First Page
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.firstPage || ''}
                    onChange={(e) => setFormData({ ...formData, firstPage: e.target.value, lastPage: lastPageFor(e.target.value, formData.pageCount) || formData.lastPage })}
                    placeholder="e.g. 17"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Last Page{formData.pageCount ? ` (PDF has ${formData.pageCount} pages)` : ''}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.lastPage || ''}
                    onChange={(e) => setFormData({ ...formData, lastPage: e.target.value })}
                    placeholder="e.g. 21"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Published Date & Sequence */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Published Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Article Display Order
                  </label>
                  <input
                    type="text"
                    value={formData.articleNumber}
                    onChange={(e) => setFormData({ ...formData, articleNumber: e.target.value })}
                    placeholder="e.g. 1"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* PDF File Link & Direct Local Upload */}
                <div className="space-y-2 md:col-span-2 bg-blue-50/40 p-4 rounded-2xl border border-blue-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                      PDF Document *
                    </label>
                    <label className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-semibold rounded-full cursor-pointer shadow-xs transition">
                      <FaUpload className="text-[10px]" />
                      <span>{uploadingPdf ? 'Uploading...' : 'Upload PDF from Computer'}</span>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <input
                    type="text"
                    required
                    value={formData.fileLink}
                    onChange={(e) => setFormData({ ...formData, fileLink: e.target.value })}
                    placeholder="/files/article_name.pdf"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />

                  {(formData.fileLink || formData.friendlyUrl) && (
                    <button
                      type="button"
                      onClick={handleReadDatesForForm}
                      disabled={readingDates}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 hover:bg-blue-50 disabled:opacity-60 text-[#03478E] text-xs font-semibold rounded-full transition"
                    >
                      <FaCalendarAlt className="text-[10px]" />
                      <span>{readingDates ? 'Reading PDF...' : 'Read dates & page count from PDF'}</span>
                    </button>
                  )}

                  {uploadStatus && (
                    <div className={`flex items-center gap-1.5 text-xs font-medium mt-1 ${uploadFailed ? 'text-red-600' : 'text-blue-700'}`}>
                      <FaCheckCircle className={`text-xs flex-shrink-0 ${uploadFailed ? 'text-red-500' : 'text-green-600'}`} />
                      <span>{uploadStatus}</span>
                    </div>
                  )}
                  <p className="text-[11px] text-gray-500">
                    Direct local URL: <code className="bg-white px-1 py-0.5 rounded border border-gray-200">/files/filename.pdf</code> (Saved automatically in <code className="bg-white px-1 py-0.5 rounded border border-gray-200">public/files/</code>).
                  </p>
                </div>

                {/* Author Affiliation */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Author Affiliation / Hospital / Department
                  </label>
                  <input
                    type="text"
                    value={formData.affiliation}
                    onChange={(e) => setFormData({ ...formData, affiliation: e.target.value })}
                    placeholder="e.g. Department of Practice of Medicine, AIM Homoeopathic College, India"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Keywords */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Keywords (Comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="e.g. Homoeopathy, Repertory, RCT, Chronic Disease"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Abstract */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Abstract
                  </label>
                  <textarea
                    rows={4}
                    value={formData.abstract}
                    onChange={(e) => setFormData({ ...formData, abstract: e.target.value })}
                    placeholder="Enter structured background, methods, results, and conclusion..."
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Checkboxes */}
                <div className="md:col-span-2 flex items-center gap-6 pt-2 bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.isCurrentIssue}
                      onChange={(e) => setFormData({ ...formData, isCurrentIssue: e.target.checked })}
                      className="rounded text-[#03478E] focus:ring-[#03478E] w-4 h-4"
                    />
                    <span>Mark as Part of Current Active Issue</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.isArchived}
                      onChange={(e) => setFormData({ ...formData, isArchived: e.target.checked })}
                      className="rounded text-[#03478E] focus:ring-[#03478E] w-4 h-4"
                    />
                    <span>Include in Archive Repository</span>
                  </label>
                </div>

              </div>

              {/* Modal Actions */}
              <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={saving}
                  className="px-5 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#03478E] hover:bg-[#023366] text-white rounded-xl text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {saving ? 'Saving...' : modalMode === 'edit' ? 'Save Changes' : 'Publish Article'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ArticlesAdmin;
